import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { workouts } from '../../services/api';
import { extractStreamRaw } from '../../utils/workoutStream';

export interface UseWorkoutStreamResult {
  streamRaw: unknown;
  loading: boolean;
  error: string | null;
  /** Force refetch — e.g. retry after error. */
  reload: () => void;
}

/**
 * Loads GET /workout/{id}/data once per open panel and keeps it in memory.
 * Survives analyze ↔ summary tab switches (parent owns the cache).
 * Freed automatically when WorkoutDetailPanel unmounts.
 */
export function useWorkoutStream(workoutId: number, enabled: boolean): UseWorkoutStreamResult {
  const { t } = useTranslation();
  const [streamRaw, setStreamRaw] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGenRef = useRef(0);
  const loadedWorkoutIdRef = useRef<number | null>(null);
  const hasCachedStreamRef = useRef(false);

  const fetchStream = useCallback(
    async (force: boolean) => {
      if (!enabled) return;
      if (!force && loadedWorkoutIdRef.current === workoutId && hasCachedStreamRef.current) return;

      const gen = ++fetchGenRef.current;
      setLoading(true);
      setError(null);

      try {
        const res = await workouts.getWorkoutData(workoutId);
        if (gen !== fetchGenRef.current) return;
        setStreamRaw(extractStreamRaw(res.data));
        loadedWorkoutIdRef.current = workoutId;
        hasCachedStreamRef.current = true;
      } catch {
        if (gen !== fetchGenRef.current) return;
        setStreamRaw(null);
        loadedWorkoutIdRef.current = null;
        hasCachedStreamRef.current = false;
        setError(t('workoutDetail.map.streamFailed'));
      } finally {
        if (gen === fetchGenRef.current) setLoading(false);
      }
    },
    [enabled, workoutId, t],
  );

  /** Drop cache when viewing a different workout. */
  useEffect(() => {
    fetchGenRef.current += 1;
    loadedWorkoutIdRef.current = null;
    hasCachedStreamRef.current = false;
    setStreamRaw(null);
    setError(null);
    setLoading(false);
  }, [workoutId]);

  /** Prefetch in background when analyze tab is available (even on summary tab). */
  useEffect(() => {
    if (!enabled) return;
    void fetchStream(false);
  }, [enabled, workoutId, fetchStream]);

  const reload = useCallback(() => {
    loadedWorkoutIdRef.current = null;
    hasCachedStreamRef.current = false;
    setStreamRaw(null);
    void fetchStream(true);
  }, [fetchStream]);

  return { streamRaw, loading, error, reload };
}
